import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLotteryContract } from '../contracts/hooks';
import { LotteryDetails } from '../contracts/interfaces';

interface LotteryContextType {
  lotteryDetails: LotteryDetails | null;
  isLoading: boolean;
  error: string | null;
  purchaseTicket: (numbers: number[], value: bigint) => Promise<boolean>;
  refreshLotteryDetails: () => Promise<void>;
}

const LotteryContext = createContext<LotteryContextType | undefined>(undefined);

export function LotteryProvider({ children }: { children: React.ReactNode }) {
  const [lotteryDetails, setLotteryDetails] = useState<LotteryDetails | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { getLotteryDetails, purchaseTicket: purchaseTicketRaw } =
    useLotteryContract();

  const refreshLotteryDetails = async () => {
    try {
      setIsLoading(true);
      const details = await getLotteryDetails();
      setLotteryDetails(details);
      setError(null);
    } catch (err) {
      setError('Failed to fetch lottery details');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const purchaseTicket = async (numbers: number[], value: bigint) => {
    return purchaseTicketRaw({ numbers, value });
  };

  useEffect(() => {
    refreshLotteryDetails();
  }, []);

  return (
    <LotteryContext.Provider
      value={{
        lotteryDetails,
        isLoading,
        error,
        purchaseTicket,
        refreshLotteryDetails,
      }}
    >
      {children}
    </LotteryContext.Provider>
  );
}

export function useLottery() {
  const context = useContext(LotteryContext);
  if (context === undefined) {
    throw new Error('useLottery must be used within a LotteryProvider');
  }
  return context;
}
